import { useEffect, useState } from 'react';
import {
  apiClient,
  type InviteRow,
  type MemberRow,
  type ProjectDetail,
  type WorkspaceRole,
  ApiError,
} from '@/lib/api/client';
// Note: ProjectDetail.created_by is returned by the API but not yet in the
// TypeScript type. Cast at use-site. Will be added properly in a follow-up.
import { cn } from '@/lib/utils';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { IdentityEditor } from '@/components/IdentityEditor';
import { UserAvatar } from '@/components/UserAvatar';

interface Props {
  projectId: string;
  myUserId: string;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}

type ProjectDetailWithCreator = ProjectDetail & { created_by?: string };

const ROLE_OPTIONS: WorkspaceRole[] = ['owner', 'admin', 'member', 'viewer'];

export function ProjectSettingsDrawer({
  projectId,
  myUserId,
  onClose,
  onChanged,
  onDeleted,
}: Props) {
  const [tab, setTab] = useState<'general' | 'members' | 'invites'>('general');
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const myRole = project?.role ?? null;
  const canAdmin = myRole === 'owner' || myRole === 'admin';
  const dialogRef = useDialogFocus<HTMLDivElement>();

  async function refresh() {
    const [p, m, i] = await Promise.all([
      apiClient.getProject(projectId),
      apiClient.listProjectMembers(projectId),
      canAdmin
        ? apiClient.listProjectInvites(projectId).catch(() => ({ invites: [] }))
        : Promise.resolve({ invites: [] }),
    ]);
    setProject(p);
    setMembers(m.members);
    setInvites(i.invites);
  }

  useEffect(() => {
    refresh().catch(console.error);
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="proj-settings-title"
      aria-describedby="proj-settings-desc"
      className="fixed inset-0 z-50 flex"
    >
      <div className="flex-1 bg-black/60 animate-in fade-in-0 duration-150" onClick={onClose} aria-hidden />
      <div id="proj-settings-desc" className="sr-only">Project settings drawer — rename project, manage members, and send invites.</div>
      <div ref={dialogRef} className="flex w-full max-w-2xl flex-col bg-popover shadow-2xl animate-in slide-in-from-right-4 fade-in-0 duration-150">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Project settings
            </div>
            <h2 id="proj-settings-title" className="truncate text-lg font-semibold">{project?.name ?? 'Loading…'}</h2>
            {project && (
              <div className="truncate font-mono text-[10px] text-muted-foreground">
                /p/{project.slug} · in {project.ws_name} · your role: {myRole ?? '—'}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
            aria-label="Close settings"
          >
            Esc
          </button>
        </header>
        <nav className="flex border-b border-border px-6">
          {(['general', 'members', 'invites'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'border-b-2 px-4 py-3 text-sm capitalize transition-colors',
                tab === t
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t}
            </button>
          ))}
        </nav>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {tab === 'general' && project && (
            <GeneralTab
              project={project}
              canEdit={canAdmin}
              myUserId={myUserId}
              onSaved={() => {
                refresh();
                onChanged();
              }}
              onDeleted={onDeleted}
            />
          )}
          {tab === 'members' && project && (
            <MembersTab
              project_id={projectId}
              members={members}
              myUserId={myUserId}
              canAdmin={canAdmin}
              onChanged={() => {
                refresh();
                onChanged();
              }}
            />
          )}
          {tab === 'invites' && canAdmin && (
            <InvitesTab project_id={projectId} invites={invites} onChanged={refresh} />
          )}
          {tab === 'invites' && !canAdmin && (
            <div className="text-sm text-muted-foreground">Admins only.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GeneralTab({
  project,
  canEdit,
  myUserId,
  onSaved,
  onDeleted,
}: {
  project: ProjectDetailWithCreator;
  canEdit: boolean;
  myUserId: string;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = name.trim() && name.trim() !== project.name;
  // Creator-only delete per share-model rule (sub-step 5+).
  // The API enforces it too; this just hides the affordance for non-creators.
  const isCreator = project.created_by === myUserId;

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiClient.updateProject(project.project_id, { name: name.trim() });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Field label="Identity">
        <IdentityEditor
          name={project.name}
          image_key={project.image_key}
          emoji={project.emoji}
          color={project.color}
          disabled={!canEdit}
          onUploadImage={async (file) => {
            await apiClient.uploadProjectImage(project.project_id, file);
            onSaved();
          }}
          onRemoveImage={async () => {
            await apiClient.deleteProjectImage(project.project_id);
            onSaved();
          }}
          onSaveEmoji={async (emoji) => {
            await apiClient.updateProject(project.project_id, { emoji });
            onSaved();
          }}
          onSaveColor={async (color) => {
            await apiClient.updateProject(project.project_id, { color });
            onSaved();
          }}
        />
      </Field>
      <Field label="Project name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit || saving}
          maxLength={64}
          className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60"
        />
      </Field>
      <Field label="Lives in workspace">
        <div className="text-sm text-muted-foreground">{project.ws_name}</div>
      </Field>
      {err && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
          {err}
        </div>
      )}
      {canEdit && (
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
      <CloseToggle
        project={project}
        canManage={project.role === 'owner' || project.role === 'admin'}
        onSaved={onSaved}
      />
      <DangerZone
        project={project}
        myUserId={myUserId}
        isCreator={isCreator}
        onDeleted={onDeleted}
      />
    </div>
  );
}

function CloseToggle({
  project,
  canManage,
  onSaved,
}: {
  project: ProjectDetailWithCreator;
  canManage: boolean;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isClosed = !!project.closed_at;

  async function toggle() {
    setBusy(true);
    setErr(null);
    try {
      if (isClosed) await apiClient.reopenProject(project.project_id);
      else await apiClient.closeProject(project.project_id);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  if (!canManage) return null;

  return (
    <div className="mt-8 space-y-3 rounded-lg border border-border bg-card/40 p-5">
      <div className="space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {isClosed ? 'Closed project' : 'Close project'}
        </div>
        <p className="text-sm text-muted-foreground">
          {isClosed
            ? 'This project is read-only. Reopen to let members add tasks, comments, notes, and links again.'
            : 'Mark this project closed. Members can still view everything, but no new tasks, comments, notes, or links until you reopen.'}
        </p>
      </div>
      {err && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
          {err}
        </div>
      )}
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={cn(
          'rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60',
          isClosed
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border border-border text-foreground hover:border-ring/60 hover:bg-card',
        )}
      >
        {busy ? '…' : isClosed ? 'Reopen project' : 'Close project'}
      </button>
    </div>
  );
}

function DangerZone({
  project,
  myUserId: _myUserId,
  isCreator,
  onDeleted,
}: {
  project: ProjectDetailWithCreator;
  myUserId: string;
  isCreator: boolean;
  onDeleted: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const matches = confirmText === project.name;

  async function destroy() {
    if (!matches) return;
    setBusy(true);
    setErr(null);
    try {
      await apiClient.deleteProject(project.project_id, confirmText);
      onDeleted();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to delete');
      setBusy(false);
    }
  }

  return (
    <div className="mt-8 space-y-3 rounded-lg border border-destructive/30 bg-destructive/[0.04] p-5">
      <div className="space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-destructive/80">
          Danger zone
        </div>
        <div className="text-sm font-semibold text-foreground">Delete this project</div>
        <p className="text-xs text-muted-foreground">
          Soft-deletes the project. It's restorable from <strong>Trash</strong> for 30 days; after
          that it's permanently removed by our daily cleanup. Other members lose access immediately.
        </p>
      </div>
      {!isCreator ? (
        <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          Only the project's creator can delete it. Ask them if you want this removed.
        </div>
      ) : (
        <>
          <div className="space-y-1">
            <label
              htmlFor="confirm-delete"
              className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
            >
              Type{' '}
              <span className="font-mono text-foreground">"{project.name}"</span> to confirm
            </label>
            <input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={busy}
              placeholder={project.name}
              className="block w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm focus:border-destructive focus:outline-none focus:ring-2 focus:ring-destructive/30 disabled:opacity-60"
            />
          </div>
          {err && (
            <div role="alert" className="text-xs text-destructive">
              {err}
            </div>
          )}
          <div className="space-y-1">
            <button
              type="button"
              onClick={destroy}
              disabled={!matches || busy}
              className="rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete project'}
            </button>
            <p className="font-mono text-[10px] text-muted-foreground/70">
              Restorable for 30 days from Trash.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function MembersTab({
  project_id,
  members,
  myUserId,
  canAdmin,
  onChanged,
}: {
  project_id: string;
  members: MemberRow[];
  myUserId: string;
  canAdmin: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Two-step inline confirm (matches SourcesView.destroy). First click sets
  // pendingRemove + 3s revert; second click within window commits.
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  async function setRole(user_id: string, role: WorkspaceRole) {
    setBusy(user_id);
    setErr(null);
    try {
      await apiClient.updateProjectMemberRole(project_id, user_id, role);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to change role');
    } finally {
      setBusy(null);
    }
  }
  async function remove(user_id: string) {
    if (pendingRemove !== user_id) {
      setPendingRemove(user_id);
      setTimeout(() => {
        setPendingRemove((curr) => (curr === user_id ? null : curr));
      }, 3000);
      return;
    }
    setPendingRemove(null);
    setBusy(user_id);
    setErr(null);
    try {
      await apiClient.removeProjectMember(project_id, user_id);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to remove');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      {err && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
          {err}
        </div>
      )}
      {members.map((m) => (
        <div key={m.user_id} className="flex items-center justify-between rounded-md border border-border bg-card px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <UserAvatar
              email={m.email}
              image_key={m.image_key}
              avatar_url={m.avatar_url}
              size={32}
              title={m.email}
            />
            <div className="min-w-0">
              <div className="truncate text-sm text-foreground">{m.email}</div>
              {m.name && <div className="truncate text-xs text-muted-foreground">{m.name}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canAdmin ? (
              <select
                value={m.role}
                onChange={(e) => setRole(m.user_id, e.target.value as WorkspaceRole)}
                disabled={busy === m.user_id}
                className="rounded-md border border-border bg-card px-2 py-1 text-xs"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            ) : (
              <span className="font-mono text-xs text-muted-foreground">{m.role}</span>
            )}
            {(canAdmin || m.user_id === myUserId) && (
              <>
                <button
                  onClick={() => remove(m.user_id)}
                  disabled={busy === m.user_id}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-60',
                    pendingRemove === m.user_id
                      ? 'border-destructive/70 bg-destructive/15 text-destructive'
                      : 'border-border text-muted-foreground hover:border-destructive/60 hover:text-destructive',
                  )}
                  aria-describedby={pendingRemove === m.user_id ? `member-${m.user_id}-confirm` : undefined}
                >
                  {pendingRemove === m.user_id
                    ? 'Confirm · 3s'
                    : m.user_id === myUserId
                      ? 'Leave'
                      : 'Remove'}
                </button>
                {pendingRemove === m.user_id && (
                  <span id={`member-${m.user_id}-confirm`} role="status" className="sr-only">
                    Click again within 3 seconds to confirm {m.user_id === myUserId ? 'leaving' : `removing ${m.email}`}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function InvitesTab({
  project_id,
  invites,
  onChanged,
}: {
  project_id: string;
  invites: InviteRow[];
  onChanged: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('member');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    if (!email.trim()) return;
    setSending(true);
    setErr(null);
    try {
      await apiClient.createProjectInvite(project_id, email.trim(), role);
      setEmail('');
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to send invite');
    } finally {
      setSending(false);
    }
  }
  async function revoke(prefix: string) {
    try {
      await apiClient.revokeProjectInvite(project_id, prefix);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to revoke');
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Invite by email
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="block flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-ring focus:outline-none"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as WorkspaceRole)}
            className="rounded-md border border-border bg-background px-2 text-sm"
          >
            <option value="admin">admin</option>
            <option value="member">member</option>
            <option value="viewer">viewer</option>
          </select>
          <button
            onClick={send}
            disabled={!email.trim() || sending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {err && (
          <div role="alert" className="text-sm text-destructive">
            {err}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Pending invites
        </div>
        {invites.length === 0 ? (
          <div className="text-sm text-muted-foreground">No pending invites.</div>
        ) : (
          invites.map((i) => (
            <div
              key={i.token_hash_prefix}
              className="flex items-center justify-between rounded-md border border-border bg-card px-4 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm text-foreground">{i.invited_email}</div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  {i.role} · expires {new Date(i.expires_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => revoke(i.token_hash_prefix)}
                className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive"
              >
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
